/**
 * 고객센터 (/my/support/ · page-partner-support.php).
 *
 * - 답글: 상세의 폼을 admin-ajax(Partner_Support::AJAX_REPLY)로 보내고, 대화를 서버가 다시 그리도록 새로고침한다.
 *   알림은 새로고침 뒤에 띄운다(sessionStorage — partnerForm.js).
 * - 아크릴 주문: 수량을 바꾸면 소계 · 합계를 맞추고, 세금계산서 발행을 고르면 사업자 칸과 부가세 10% 줄을 연다.
 *   견적 종류(기타)에 수량을 넣으면 요청 내용 칸이 열리고 합계에는 들어가지 않는다.
 * - 앱 양식 신청(?view=features): 항목을 체크하면 합계를 맞추고, 세금계산서 발행을 고르면 사업자 칸과 부가세 10% 줄을 연다.
 *   이용 중 · 신청 중 항목은 서버가 disabled 로 그려 두므로 합계에 넣지 않는다. 접수되면 폼을 감추고 입금 안내를 보여 준다.
 * - 양식 설정 열기([data-wizard-link], 신청 화면 · 신청 진행 카드): 매장 사이트의 일회용 통행 주소를 admin-ajax 로 받아
 *   새 창에 연다. 창은 클릭 시점에 먼저 열어 팝업 차단을 피하고, 실패하면 닫는다. 매장 사이트가 그 주소에서 로그인 쿠키를
 *   심으므로 아이디 · 비밀번호를 다시 넣지 않는다.
 * - 주문 진행(상세, template-parts/support-order.php): 금액 승인 · 시안 승인 · 수정 요청 버튼을 admin-ajax(Acrylic_Order)로
 *   보내고 새로고침한다.
 *   종류 썸네일을 누르면 크게 보기 모달(#order-image-modal)에 그림 · 제목을 넣는다. 여는 건 initModal.js 가
 *   [data-modal-open] 으로 하므로, 폼에서 먼저 받아 내용만 바꿔 둔다.
 *   접수되면 폼을 감추고 입금 안내(statepage)를 보여 준다. 첨부는 공통 파일 첨부(attachfield)가 input 에 넣어 둔다.
 * 보내는 동안 버튼에 진행 중 표시(aria-busy)를 하고, 실패는 폼 안 안내(.notice)에 적는다.
 */

import { busy } from './busyButton';
import {
  postForm,
  reloadWith as reloadWithKey,
  setNotice,
  showFlash,
  showToast,
} from './partnerForm';

const FLASH_KEY = 'qrshop_support_flash';
const PHONE_PATTERN = /^0\d{8,10}$/;
// Acrylic_Order 의 매장 AJAX 액션 이름 (template-parts/support-order.php 의 data-order-action 과 같다)
const ACTION_QUOTE_APPROVE = 'qrshop_acrylic_quote_approve';
const ACTION_PROOF_APPROVE = 'qrshop_acrylic_proof_approve';
const ACTION_PROOF_REVISE = 'qrshop_acrylic_proof_revise';
// 매장 사이트 양식 설정(위저드) 자동 로그인 링크 (Partner_Support::AJAX_WIZARD_LINK)
const ACTION_WIZARD_LINK = 'qrshop_wizard_link';

/**
 * 서버가 준 절대 주소(home_url 기준, 라이브 도메인)를 지금 보고 있는 도메인의 경로로 바꾼다.
 * 개발 중 BrowserSync(localhost) 로 보면 HTML 의 링크는 바뀌지만 AJAX 응답은 그대로라
 * 라이브 도메인으로 튀어 나간다. 같은 사이트의 경로만 쓰므로 어느 도메인에서든 맞는다.
 */
const samePath = (url) => {
  try {
    const parsed = new URL(url, window.location.href);

    return parsed.pathname + parsed.search + parsed.hash;
  } catch (error) {
    return url;
  }
};

const initReply = (root, form) => {
  const status = form.querySelector('[data-support-status]');
  const submit = form.querySelector('[type="submit"]');
  const textarea = form.elements.namedItem('text');

  form.addEventListener('input', () => setNotice(status, ''));

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const text = textarea instanceof HTMLTextAreaElement ? textarea.value.trim() : '';

    if ('' === text) {
      setNotice(status, '내용을 입력해 주세요.');
      textarea?.focus();
      return;
    }

    const release = busy(submit, '남기는 중…');

    try {
      const result = await postForm(root, form.dataset.action || '', {
        id: form.elements.namedItem('id')?.value || '',
        text,
      });
      reloadWithKey(FLASH_KEY, result.message || '답글을 남겼습니다.');
    } catch (error) {
      release();
      setNotice(status, error.message);
    }
  });
};

const initOrder = (root, form) => {
  const status = form.querySelector('[data-support-status]');
  const submit = form.querySelector('[type="submit"]');
  const total = form.querySelector('[data-order-total]');
  const taxFields = form.querySelector('[data-order-tax-fields]');
  const done = root.querySelector('[data-order-done]');
  const won = (value) => `${Number(value).toLocaleString('ko-KR')}원`;
  const number = (value) => Number(value).toLocaleString('ko-KR');
  // 배송비 — 설정값. 종류를 하나라도 담았을 때만 합계에 더한다 (서버도 같은 값을 쓴다)
  const shipping = Number(form.querySelector('[data-order-shipping]')?.dataset.orderShipping) || 0;
  const totalLabel = form.querySelector('[data-order-total-label]');
  const vatRow = form.querySelector('[data-order-vat-row]');
  const vat = form.querySelector('[data-order-vat]');
  const dueRow = form.querySelector('[data-order-due-row]');
  const due = form.querySelector('[data-order-due]');
  const pendingHint = form.querySelector('[data-order-pending]');
  const quoteFields = form.querySelector('[data-order-quote-fields]');
  const VAT_RATE = 0.1;

  const isTax = () => '1' === form.querySelector('input[name="tax_invoice"]:checked')?.value;

  /**
   * 소계 · 합계 · 부가세 · 입금 금액을 다시 계산해 그린다.
   * 견적 종류(기타)는 수량만 세고 금액에는 넣지 않는다 — 관리자가 견적을 낸 뒤 확정된다.
   *
   * @return {{ sum: number, count: number, pending: boolean }} 종류 소계 · 담은 종류 수 · 견적 대기 여부
   */
  const recalc = () => {
    let sum = 0;
    let count = 0;
    let pending = false;

    form.querySelectorAll('[data-order-price]').forEach((row) => {
      const price = Number(row.dataset.orderPrice) || 0;
      const isQuote = '1' === row.dataset.orderQuote;
      const input = row.querySelector('input[type="number"]');
      const qty = Math.max(0, Math.floor(Number(input?.value) || 0));
      const cell = row.querySelector('[data-order-subtotal]');

      if (qty > 0) {
        count += 1;
      }

      if (isQuote) {
        pending = pending || qty > 0;

        if (cell) {
          cell.textContent = qty > 0 ? '확인 후 안내' : '—';
        }

        return;
      }

      const subtotal = price * qty;
      sum += subtotal;

      if (cell) {
        cell.textContent = won(subtotal);
      }
    });

    const grand = count > 0 ? sum + shipping : 0;
    const tax = isTax();
    const vatAmount = tax ? Math.round(grand * VAT_RATE) : 0;

    if (total) {
      total.textContent = number(grand);
    }

    if (totalLabel) {
      totalLabel.textContent = tax ? '공급가액' : '합계';
    }

    if (vatRow) {
      vatRow.hidden = !tax;
    }

    if (vat) {
      vat.textContent = number(vatAmount);
    }

    if (dueRow) {
      dueRow.hidden = !tax;
    }

    // 발행이면 입금 금액이 최종 금액 — 강조를 공급가액에서 입금 금액으로 옮긴다 (support.css --tax)
    form.querySelector('.order-form__amounts')?.classList.toggle('order-form__amounts--tax', tax);

    if (due) {
      due.textContent = number(grand + vatAmount);
    }

    if (pendingHint) {
      pendingHint.hidden = !pending;
    }

    if (quoteFields) {
      quoteFields.hidden = !pending;
    }

    return { sum, count, pending };
  };

  const toggleTax = () => {
    if (taxFields) {
      taxFields.hidden = !isTax();
    }
  };

  form.addEventListener('input', () => {
    recalc();
    setNotice(status, '');
  });
  form.addEventListener('change', () => {
    toggleTax();
    recalc();
    setNotice(status, '');
  });
  form.addEventListener('attachfield:error', (event) =>
    setNotice(status, event.detail?.message || '')
  );

  recalc();
  toggleTax();

  // 종류 이미지 크게 보기 — 모달은 하나, 누른 썸네일의 그림 · 제목으로 바꿔 넣는다
  const imageModal = root.querySelector('#order-image-modal');
  const imageModalImg = imageModal?.querySelector('[data-order-image-modal-img]');
  const imageModalTitle = imageModal?.querySelector('[data-order-image-modal-title]');

  form.addEventListener('click', (event) => {
    const thumb = event.target.closest?.('[data-order-image]');

    if (!thumb || !(imageModalImg instanceof HTMLImageElement)) {
      return;
    }

    const title = thumb.dataset.orderImageTitle || '';
    imageModalImg.src = thumb.dataset.orderImage || '';
    imageModalImg.alt = title;

    if (imageModalTitle) {
      imageModalTitle.textContent = title || '종류 이미지';
    }
  });

  const value = (name) => {
    const element = form.elements.namedItem(name);

    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
      ? element.value.trim()
      : '';
  };

  const findProblem = () => {
    const state = recalc();

    if (0 === state.count) {
      return {
        message: '주문할 종류의 수량을 입력해 주세요.',
        focus: form.querySelector('input[type="number"]'),
      };
    }

    if (!form.querySelector('input[name="realtime_call"]:checked')) {
      return {
        message: '실시간 호출 기능 사용 여부를 골라 주세요.',
        focus: form.querySelector('input[name="realtime_call"]'),
      };
    }

    if (state.pending && '' === value('quote_note')) {
      return {
        message: '기타 종류는 어떤 아크릴이 필요한지 요청 내용을 적어 주세요.',
        focus: form.elements.namedItem('quote_note'),
      };
    }

    if ('' === value('recipient')) {
      return {
        message: '받는 분 이름을 입력해 주세요.',
        focus: form.elements.namedItem('recipient'),
      };
    }

    if (!PHONE_PATTERN.test(value('phone').replace(/\D/g, ''))) {
      return {
        message: '연락처를 01012345678 형식(숫자만)으로 입력해 주세요.',
        focus: form.elements.namedItem('phone'),
      };
    }

    if ('' === value('address')) {
      return { message: '배송지 주소를 입력해 주세요.', focus: form.elements.namedItem('address') };
    }

    if (isTax()) {
      if ('' === value('business_name')) {
        return {
          message: '사업자명을 입력해 주세요.',
          focus: form.elements.namedItem('business_name'),
        };
      }

      if (!/^\d{10}$/.test(value('business_number').replace(/\D/g, ''))) {
        return {
          message: '사업자등록번호 10자리(숫자만)를 입력해 주세요.',
          focus: form.elements.namedItem('business_number'),
        };
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value('business_email'))) {
        return {
          message: '세금계산서를 받을 이메일을 확인해 주세요.',
          focus: form.elements.namedItem('business_email'),
        };
      }

      const file = form.querySelector('input[name="business_file[]"]');

      if (!(file instanceof HTMLInputElement) || 0 === file.files.length) {
        return { message: '세금계산서 발행에는 사업자등록증 첨부가 필요합니다.', focus: null };
      }
    }

    return null;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const problem = findProblem();

    if (problem) {
      setNotice(status, problem.message);
      problem.focus?.focus();
      return;
    }

    const release = busy(submit, '접수 중…');

    try {
      const result = await postForm(root, form.dataset.action || '', new FormData(form));

      form.hidden = true;

      if (done) {
        const bank = done.querySelector('[data-order-bank]');
        const link = done.querySelector('[data-order-link]');
        const text = done.querySelector('[data-order-done-text]');

        if (bank) {
          if (result.pending) {
            bank.textContent =
              '기타 종류 금액을 확인한 뒤 입금 금액과 계좌를 안내드립니다. 안내가 오면 고객센터에서 금액을 승인해 주세요.';
          } else {
            const lines = [result.bank || ''];

            if (Number(result.vat) > 0) {
              lines.push(`공급가액: ${won(result.total || 0)}`, `부가세(10%): ${won(result.vat)}`);
            }

            lines.push(`입금 금액: ${won(result.amount_due || result.total || 0)}`);
            bank.textContent = lines.join('\n');
          }
        }

        if (text && result.pending) {
          text.textContent =
            '기타 종류가 있어 금액을 먼저 확인합니다. 확인되면 메일과 고객센터로 안내드립니다.';
        }

        if (link instanceof HTMLAnchorElement && result.url) {
          link.href = samePath(result.url);
        }

        done.hidden = false;
        done.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      showToast(result.message || '주문이 접수되었습니다.');
    } catch (error) {
      release();
      setNotice(status, error.message);
    }
  });
};

/** 앱 양식 신청 — 항목 체크 합계 · 세금계산서 · 접수 */
const initFeature = (root, form) => {
  const status = form.querySelector('[data-support-status]');
  const submit = form.querySelector('[type="submit"]');
  const total = form.querySelector('[data-order-total]');
  const totalLabel = form.querySelector('[data-order-total-label]');
  const vatRow = form.querySelector('[data-order-vat-row]');
  const vat = form.querySelector('[data-order-vat]');
  const dueRow = form.querySelector('[data-order-due-row]');
  const due = form.querySelector('[data-order-due]');
  const taxFields = form.querySelector('[data-order-tax-fields]');
  const done = root.querySelector('[data-feature-done]');
  const won = (value) => `${Number(value).toLocaleString('ko-KR')}원`;
  const number = (value) => Number(value).toLocaleString('ko-KR');
  const VAT_RATE = 0.1;

  const isTax = () => '1' === form.querySelector('input[name="tax_invoice"]:checked')?.value;

  /**
   * 체크한 항목의 금액을 더해 합계 · 부가세 · 입금 금액을 그린다.
   *
   * @return {{ sum: number, count: number }} 합계 · 고른 항목 수
   */
  const recalc = () => {
    let sum = 0;
    let count = 0;

    form.querySelectorAll('input[name="features[]"]:checked').forEach((input) => {
      if (input.disabled) {
        return;
      }

      const item = input.closest('[data-feature-price]');
      sum += Number(item?.dataset.featurePrice) || 0;
      count += 1;
    });

    const tax = isTax();
    const vatAmount = tax ? Math.round(sum * VAT_RATE) : 0;

    if (total) {
      total.textContent = number(sum);
    }

    // 라벨은 서버가 그린 "입금 금액" 그대로 — 세금계산서 선택이 없어 공급가액 · 합계 구분이 없다
    if (totalLabel && tax) {
      totalLabel.textContent = '공급가액';
    }

    if (vatRow) {
      vatRow.hidden = !tax;
    }

    if (vat) {
      vat.textContent = number(vatAmount);
    }

    if (dueRow) {
      dueRow.hidden = !tax;
    }

    if (due) {
      due.textContent = number(sum + vatAmount);
    }

    form.querySelector('.order-form__amounts')?.classList.toggle('order-form__amounts--tax', tax);

    return { sum, count };
  };

  const toggleTax = () => {
    if (taxFields) {
      taxFields.hidden = !isTax();
    }
  };

  form.addEventListener('input', () => {
    recalc();
    setNotice(status, '');
  });
  // 전체 선택 — 고를 수 있는 항목(disabled 제외)을 모두 켠다. 이미 다 켜져 있으면 모두 끈다 (버튼 글자도 따라 바뀐다)
  const selectAll = form.querySelector('[data-feature-select-all]');
  const selectable = () =>
    Array.from(form.querySelectorAll('input[name="features[]"]:not(:disabled)'));
  const syncSelectAll = () => {
    if (!selectAll) {
      return;
    }

    const boxes = selectable();
    const all = boxes.length > 0 && boxes.every((box) => box.checked);
    selectAll.textContent = all ? '전체 해제' : '전체 선택';
    selectAll.hidden = 0 === boxes.length;
  };

  selectAll?.addEventListener('click', () => {
    const boxes = selectable();
    const all = boxes.length > 0 && boxes.every((box) => box.checked);
    boxes.forEach((box) => {
      box.checked = !all;
    });
    recalc();
    syncSelectAll();
    setNotice(status, '');
  });

  form.addEventListener('change', () => {
    toggleTax();
    recalc();
    syncSelectAll();
    setNotice(status, '');
  });
  form.addEventListener('attachfield:error', (event) =>
    setNotice(status, event.detail?.message || '')
  );

  recalc();
  toggleTax();
  syncSelectAll();

  const value = (name) => {
    const element = form.elements.namedItem(name);

    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
      ? element.value.trim()
      : '';
  };

  const findProblem = () => {
    if (0 === recalc().count) {
      return {
        message: '신청할 기능을 하나 이상 골라 주세요.',
        focus: form.querySelector('input[name="features[]"]:not(:disabled)'),
      };
    }

    if (isTax()) {
      if ('' === value('business_name')) {
        return {
          message: '사업자명을 입력해 주세요.',
          focus: form.elements.namedItem('business_name'),
        };
      }

      if (!/^\d{10}$/.test(value('business_number').replace(/\D/g, ''))) {
        return {
          message: '사업자등록번호 10자리(숫자만)를 입력해 주세요.',
          focus: form.elements.namedItem('business_number'),
        };
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value('business_email'))) {
        return {
          message: '세금계산서를 받을 이메일을 확인해 주세요.',
          focus: form.elements.namedItem('business_email'),
        };
      }

      const file = form.querySelector('input[name="business_file[]"]');

      if (!(file instanceof HTMLInputElement) || 0 === file.files.length) {
        return { message: '세금계산서 발행에는 사업자등록증 첨부가 필요합니다.', focus: null };
      }
    }

    return null;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const problem = findProblem();

    if (problem) {
      setNotice(status, problem.message);
      problem.focus?.focus();
      return;
    }

    const release = busy(submit, '접수 중…');

    try {
      const result = await postForm(root, form.dataset.action || '', new FormData(form));

      form.hidden = true;

      if (done) {
        const bank = done.querySelector('[data-feature-bank]');
        const link = done.querySelector('[data-feature-link]');

        if (bank) {
          const lines = [result.bank || ''];

          if (Number(result.vat) > 0) {
            lines.push(`공급가액: ${won(result.total || 0)}`, `부가세(10%): ${won(result.vat)}`);
          }

          lines.push(`입금 금액: ${won(result.amount_due || result.total || 0)}`);
          bank.textContent = lines.join('\n');
        }

        if (link instanceof HTMLAnchorElement && result.url) {
          link.href = samePath(result.url);
        }

        done.hidden = false;
        done.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      showToast(result.message || '신청이 접수되었습니다.');
    } catch (error) {
      release();
      setNotice(status, error.message);
    }
  });
};

/** 양식 설정 열기 — 매장 사이트의 일회용 통행 주소를 받아 새 창으로 */
export const initWizardLinks = (root) => {
  root.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-wizard-link]');

    if (!button || button.disabled) {
      return;
    }

    // 팝업 차단 회피: 클릭 제스처 시점에 창을 먼저 확보해 둔다
    const win = window.open('', '_blank');
    const release = busy(button, '여는 중…');

    try {
      const result = await postForm(root, ACTION_WIZARD_LINK, {});

      if (!result.url) {
        throw new Error('양식 설정 주소를 받지 못했습니다.');
      }

      if (win) {
        win.location = result.url;
      } else {
        window.location.href = result.url;
      }
    } catch (error) {
      win?.close();
      showToast(error.message || '양식 설정을 열지 못했습니다.', 'error');
    } finally {
      release();
    }
  });
};

/** 주문 진행 카드 — 금액 승인 · 시안 승인 · 수정 요청 */
const initOrderProgress = (root, panel) => {
  const status = panel.querySelector('[data-support-status]');
  const confirm = panel.querySelector('[data-order-confirm]');
  const approve = panel.querySelector(`[data-order-action="${ACTION_PROOF_APPROVE}"]`);

  if (confirm instanceof HTMLInputElement && approve) {
    confirm.addEventListener('change', () => {
      approve.disabled = !confirm.checked;
    });
  }

  panel.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-order-action]');

    if (!button || button.disabled) {
      return;
    }

    const action = button.dataset.orderAction || '';
    const fields = { id: panel.dataset.id || '' };

    if (ACTION_PROOF_APPROVE === action) {
      if (!(confirm instanceof HTMLInputElement) || !confirm.checked) {
        setNotice(status, '확인했다는 항목에 체크해 주세요.');
        return;
      }

      fields.version = button.dataset.orderVersion || '';
      fields.confirm = '1';
    }

    if (ACTION_PROOF_REVISE === action) {
      const textarea = panel.querySelector('[data-order-revise-text]');
      const text = textarea instanceof HTMLTextAreaElement ? textarea.value.trim() : '';

      if ('' === text) {
        setNotice(status, '어디를 어떻게 고칠지 적어 주세요.');
        textarea?.focus();
        return;
      }

      fields.text = text;
    }

    if (ACTION_QUOTE_APPROVE === action && !window.confirm('안내받은 금액으로 확정할까요?')) {
      return;
    }

    const release = busy(button, '처리 중…');

    try {
      const result = await postForm(root, action, fields);
      reloadWithKey(FLASH_KEY, result.message || '처리했습니다.');
    } catch (error) {
      release();
      setNotice(status, error.message);
    }
  });
};

const InitPartnerSupport = () => {
  const root = document.querySelector('[data-partner-support]');

  if (!root) {
    return;
  }

  showFlash(FLASH_KEY);

  initWizardLinks(root);

  const progress = root.querySelector('[data-order-progress]');

  if (progress) {
    initOrderProgress(root, progress);
  }

  const reply = root.querySelector('[data-support-reply]');
  const order = root.querySelector('[data-support-order]');
  const feature = root.querySelector('[data-support-feature]');

  if (reply) {
    initReply(root, reply);
  }

  if (order) {
    initOrder(root, order);
  }

  if (feature) {
    initFeature(root, feature);
  }
};

export default InitPartnerSupport;
